import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useCustomQuery } from '../../graphql';
import { WorkspaceAsset } from '../domain';
import { WorkspaceRoutine } from '../../routine';

const CACHE_KEY = ['workspaceasset'];

export const useWorkspaceAsset = (
  // for now, we'll assume that only one workspace works with an Asset
  assetId: string,
  disabled?: boolean
) => {
  const queryClient = useQueryClient();

  const filter = {
    asset: { eq: assetId },
  };

  const cacheKey = [...CACHE_KEY, assetId];

  const { data, isLoading } = useCustomQuery({
    query: FIND_WORKSPACE_ASSET,
    cacheKey,
    variables: {
      filter,
      assetId,
    },
    options: {
      enabled: !!assetId && (disabled === undefined || !disabled),
    },
  });

  const workspaceAsset = useMemo(() => {
    if (!data) return undefined;

    const wsAsset: WorkspaceAsset = data.findWorkspaceAssets.items[0];
    const groupRoutinesData: WorkspaceRoutine[] =
      data.routine?.findGroupRoutinesByAssetId?.routines ?? [];
    const workspaceRoutinesData: WorkspaceRoutine[] =
      data.routine?.findWorkspaceRoutinesForAsset?.routines ?? [];

    // Create asset with combined media
    const productMedia = wsAsset?.asset?.product?.media || [];
    const assetMedia = wsAsset?.asset?.media || [];
    const addonMedia =
      wsAsset?.asset?.addons?.flatMap((addon: any) => addon?.media || []) || [];

    // Combine all media sources and deduplicate by id
    const allMedia = [...productMedia, ...assetMedia, ...addonMedia];
    const mediaMap = new Map();
    allMedia.forEach((m: any) => {
      if (m && m.id && !mediaMap.has(m.id)) {
        mediaMap.set(m.id, m);
      }
    });
    const combinedMedia = Array.from(mediaMap.values());

    const assetWithCombinedMedia = {
      ...wsAsset.asset,
      media: combinedMedia,
    };

    // Merge workspace-wide routines (isWorkspaceRoutine) into direct routines so
    // they appear on shadow asset pages without being written to workspaceasset.routines.
    const existingRoutineIds = new Set(
      wsAsset.routines?.filter(Boolean).map((r) => r.id) ?? []
    );
    const mergedRoutines = [
      ...(wsAsset.routines?.filter(Boolean) ?? []),
      ...workspaceRoutinesData.filter((r) => !existingRoutineIds.has(r.id)),
    ];

    if (wsAsset?.groups?.length && groupRoutinesData?.length) {
      // Create a new workspaceAsset with merged group routines and combined media
      return {
        ...wsAsset,
        routines: mergedRoutines,
        asset: assetWithCombinedMedia,
        groups: wsAsset.groups.filter(Boolean).map((group) => {
          // Filter routines that belong to this specific group
          const groupRoutines = groupRoutinesData.filter((routine) => {
            // Check if routine.groups contains the current group.id
            return routine.groups?.some(
              (routineGroup) => routineGroup.id === group.id
            );
          });

          // Return updated group with routines
          return {
            ...group,
            routines: groupRoutines,
          };
        }),
      } as WorkspaceAsset;
    } else {
      // No groups or routines to merge, but still merge media
      return {
        ...wsAsset,
        routines: mergedRoutines,
        groups: wsAsset.groups?.filter(Boolean),
        asset: assetWithCombinedMedia,
      } as WorkspaceAsset;
    }
  }, [data]);

  const invalidateQuery = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: cacheKey, exact: false });
  }, [queryClient, assetId]);

  return {
    workspaceAsset,
    isLoading,
    invalidateQuery,
  };
};

// Exported for useWorkspaceAsset.spec.ts: pinning the query shape.
export const FIND_WORKSPACE_ASSET = `
  query findWorkspaceAssets($assetId: ID!, $filter: WorkspaceAssetFilter) {
    routine {
      findGroupRoutinesByAssetId(assetId: $assetId) {
        routines {
          id
          order
          type
          config
          deactivated
          currentVersion {
            id
          }
          groups {
            id
          }
          permittedRoles {
            id
            name
          }
        }
      }
      findWorkspaceRoutinesForAsset(assetId: $assetId) {
        routines {
          id
          order
          type
          config
          deactivated
          isWorkspaceRoutine
          currentVersion {
            id
          }
          permittedRoles {
            id
            name
          }
        }
      }
    }
    findWorkspaceAssets(filter: $filter) {
      items {
        id
        asset {
          id
          name
          description
          details
          lastScannedAt
          coverImage {
            id
            name
            extension
            status
            type
            createdAt
            updatedAt
          }
          media {
            id
            name
            type
            extension
            status
            url
            createdAt
            updatedAt
            categories {
              id
              name
            }
          }
          lastAddress
          lastLocation {
            coordinates
          }
          updatedAt
          addons {
            id
            name
            media {
              id
              name
              type
              extension
              status
              url
              createdAt
              updatedAt
              categories {
                id
                name
              }
            }
          }
          product {
            id
            name
            media {
              id
              name
              type
              extension
              status
              url
              createdAt
              updatedAt
              categories {
                id
                name
              }
            }
            routines {
              id
              order
              type
              config
              deactivated
              currentVersion {
                id
              }
              permittedRoles {
                id
                name
              }
            }
            routineOrder {
              workspaceRoutineId
              order
            }
          }
          serialNumber
        }
        anonymousOptions {
          hideReportAndNote
        }
        routineState {
          workspaceRoutineId
          workspaceRoutineVersionId
          state
        }
        workspace {
          id
          type
        }
        routines {
          id
          order
          type
          config
          deactivated
          currentVersion {
            id
          }
          permittedRoles {
            id
            name
          }
        }
        groups {
          id
          name
        }
        accounts {
          id
        }
        routineOrder {
          workspaceRoutineId
          order
        }
      }
    }
  }
`;
